import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { call, get } from "./common";

const ACTIONS = [["order_in", "Order in (reserve)"], ["dispatch", "Dispatch"], ["cancel", "Cancel"], ["return", "Return"]];

function OrderSim({ variants, onDone }) {
  const [f, setF] = useState({ variant_id: "", qty: 1, order_ref: "TEST-WC-1001", ref: "D1", disposition: "resaleable" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async (a) => {
    const body = { variant_id: f.variant_id, qty: Number(f.qty), order_ref: f.order_ref, disposition: f.disposition, ...(a === "order_in" ? {} : { ref: f.ref }) };
    const r = await call(() => api.post(`/pim/orders/${a}`, body));
    if (r) { toast.success(r.idempotent ? "Already applied (idempotent, nothing changed)" : `${a} applied`); onDone(); }
  };
  const cls = "border rounded px-2 py-1";
  return (
    <div className="flex flex-wrap gap-2 items-end text-xs" data-testid="pim-order-sim">
      <select className={cls} value={f.variant_id} onChange={set("variant_id")} data-testid="pim-inv-variant"><option value="">Choose variant…</option>{variants.map((v) => <option key={v.id} value={v.id}>{v.sku}</option>)}</select>
      <input type="number" min="1" className={`${cls} w-16`} value={f.qty} onChange={set("qty")} data-testid="pim-inv-qty" />
      <input className={`${cls} w-32`} value={f.order_ref} onChange={set("order_ref")} placeholder="order ref" data-testid="pim-inv-ref" />
      <input className={`${cls} w-20`} value={f.ref} onChange={set("ref")} placeholder="action ref" data-testid="pim-inv-action-ref" />
      <select className={cls} value={f.disposition} onChange={set("disposition")} data-testid="pim-inv-disposition"><option value="resaleable">resaleable</option><option value="damaged">damaged</option></select>
      {ACTIONS.map(([a, label]) => <Button key={a} size="sm" variant="outline" disabled={!f.variant_id} onClick={() => go(a)} data-testid={`pim-inv-${a}`}>{label}</Button>)}
    </div>
  );
}

function Lines({ lines }) {
  return (
    <div className="overflow-x-auto"><table className="w-full min-w-[560px] text-xs" data-testid="pim-order-lines">
      <thead><tr className="text-left text-slate-500"><th className="py-1">Order</th><th>SKU</th><th>Qty</th><th>Dispatched</th><th>Cancelled</th><th>Returned</th><th>State</th></tr></thead>
      <tbody>{lines.map((l) => (
        <tr key={`${l.order_ref}-${l.variant_id}`} className="border-t" data-testid={`pim-line-${l.order_ref}-${l.sku}`}>
          <td className="py-1">{l.order_ref}</td><td>{l.sku}</td><td>{l.qty}</td><td>{l.dispatched}</td><td>{l.cancelled}</td><td>{l.returned}</td><td className="font-medium">{l.state}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

export function Inventory({ items, variants, reload }) {
  const [lines, setLines] = useState([]);
  const loadLines = useCallback(() => get("/pim/orders").then((d) => setLines(d.items)).catch(() => setLines([])), []);
  useEffect(() => { loadLines(); }, [loadLines]);
  const done = () => { reload(); loadLines(); };
  return (
    <div className="space-y-3" data-testid="pim-inventory">
      <p className="text-xs text-slate-500">Sandbox only. Stock items are links to operational products. Quantities and order actions use the existing TedOps stock ledger code in an isolated sandbox database. Live stock, EPOS and WooCommerce are not touched. Stock is reserved when an order comes in and deducted only on dispatch.</p>
      <OrderSim variants={variants} onDone={done} />
      <div className="overflow-x-auto"><table className="w-full min-w-[640px] text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1">Stock item (operational product)</th><th>Physical</th><th>Reserved</th><th>Allocated</th><th>Available to sell</th><th>Used by variants</th></tr></thead>
        <tbody>{items.map((i) => (
          <tr key={i.id} className="border-t align-top" data-testid={`pim-stock-${i.id}`}>
            <td className="py-1">{i.name}<div className="text-slate-400">{i.product_legacy_id}{i.live_reference_legacy_id ? ` · live ref ${i.live_reference_legacy_id}` : ""}</div></td>
            <td data-testid={`pim-stock-physical-${i.id}`}>{i.physical}</td><td data-testid={`pim-stock-reserved-${i.id}`}>{i.reserved}</td><td>{i.allocated}</td>
            <td className="font-semibold" data-testid={`pim-stock-available-${i.id}`}>{i.available}</td>
            <td className={i.used_by_variants.length > 1 ? "text-amber-700" : "text-slate-500"}>{i.used_by_variants.join(", ") || "—"}{i.used_by_variants.length > 1 ? " (shared)" : ""}</td>
          </tr>))}
        </tbody></table></div>
      <Lines lines={lines} />
    </div>
  );
}

export function MediaLibrary({ media, reload }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("record_type", "product_enrichment"); fd.append("record_id", "pim"); fd.append("category", "Product"); fd.append("file", file);
    const up = await call(() => api.post("/attachments/upload", fd), "Uploaded privately; waiting for malware scan");
    if (up?.attachment?.id) {
      let m = null;
      for (let i = 0; i < 10 && !m; i += 1) {
        await new Promise((res) => setTimeout(res, 3000));
        await api.post(`/attachments/${up.attachment.id}/scan/refresh`).catch(() => null);
        m = await api.post("/pim/media", { attachment_id: up.attachment.id }).then((r) => r.data).catch((x) => (x.response?.status === 422 && /not yet/.test(x.response?.data?.detail || "") ? null : (toast.error(x.response?.data?.detail || "Could not create asset"), false)));
        if (m === false) break;
      }
      if (m) { toast.success("Public optimised asset created"); reload(); } else if (m === null) toast.error("Malware scan still pending; try again shortly");
    }
    setBusy(false); e.target.value = "";
  };
  return (
    <div className="space-y-3" data-testid="pim-media">
      <p className="text-xs text-slate-500">TedOps-managed images. Originals stay private and malware-scanned; only optimised copies (metadata removed, max 2500 px) are public.</p>
      <label className="text-xs inline-flex items-center gap-2"><input type="file" accept="image/*" onChange={upload} disabled={busy} data-testid="pim-media-upload" />{busy && "Working…"}</label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{media.map((m) => (
        <div key={m.id} className="border rounded-lg p-2 text-xs" data-testid={`pim-media-${m.id}`}>
          <img src={m.public_url} alt={m.name} className="w-full h-28 object-cover rounded" />
          <div className="mt-1 font-medium truncate">{m.name}</div><div className="text-slate-500">{m.width}×{m.height}{m.future_ready_2027 ? " · 2027-ready" : ""}{m.test_data ? " · TEST" : ""}</div>
        </div>))}</div>
    </div>
  );
}
